import type { MatrixClient } from 'matrix-js-sdk';
import { ClientEvent, SyncState } from 'matrix-js-sdk';

/** Callback-Funktion für Listener, die bei Statusänderungen aufgerufen werden */
type Listener = () => void;

/**
 * Beschreibt die Art der Statusänderung im Matrix-Client.
 * Wird verwendet, um verschiedene Übergangszustände zu kategorisieren.
 */
export type MatrixStateType =
  | 'initial-sync' // null → PREPARED/SYNCING
  | 'initial-error' // null → ERROR
  | 'reconnected' // ERROR → SYNCING/CATCHUP/PREPARED
  | 'connection-lost' // SYNCING → RECONNECTING/ERROR
  | 'reconnecting' // SYNCING → RECONNECTING
  | 'stopped' // * → STOPPED
  | 'normal' // Normale Transitions (z.B. SYNCING → SYNCING, PREPARED → SYNCING)
  | null; // Kein Übergang (initial state)

/**
 * Repräsentiert den aktuellen Online-Status des Matrix-Clients.
 * Enthält sowohl technische Sync-Stati als auch abgeleitete UI-Zustände.
 */
export type MatrixOnlineState = {
  /** Aktueller Synchronisationsstatus des Matrix-Clients */
  currentSyncState: SyncState | null;
  /** Vorheriger Synchronisationsstatus (für Übergangslogik) */
  previousSyncState: SyncState | null;
  /** Art des Statusübergangs */
  status: MatrixStateType;

  /** True, wenn der Client bereit ist (PREPARED, SYNCING oder CATCHUP) */
  isReady: boolean;
  /** True, wenn ein fataler Fehler aufgetreten ist */
  hasFatalError: boolean;
  /** True, wenn die Syncing-UI angezeigt werden soll */
  showSyncingUI: boolean;
};

/**
 * Verwaltet den Status des Matrix-Clients und benachrichtigt Listener über Änderungen.
 * Implementiert ein Observer-Pattern für reactive UI-Updates.
 */
class MatrixStatusStore {
  /** Set von registrierten Listener-Funktionen */
  private listeners = new Set<Listener>();
  /** Referenz zum aktuellen Matrix-Client */
  private client: MatrixClient | null = null;
  /** Flag zur Verhinderung von gleichzeitigen Updates */
  private pendingUpdate = false;

  /** Aktueller Status-State */
  private state: MatrixOnlineState = {
    currentSyncState: null,
    previousSyncState: null,
    status: null,

    isReady: false,
    hasFatalError: false,
    showSyncingUI: true,
  };

  /**
   * Setzt den Matrix-Client und registriert Event-Listener.
   * Entfernt automatisch Listener vom alten Client, falls vorhanden.
   * @param client - Der neue Matrix-Client oder null zum Entfernen
   */
  setClient(client: MatrixClient | null): void {
    // Cleanup: Event-Listener vom alten Client entfernen
    if (this.client) {
      this.client.removeListener(ClientEvent.Sync, this.handleSyncChange);
    }

    this.client = client;

    // Status basierend auf neuem Client aktualisieren
    this.updateOnlineStatus();

    // Setup: Event-Listener für neuen Client registrieren
    if (this.client) {
      this.client.on(ClientEvent.Sync, this.handleSyncChange);
    }

    // Alle Listener über die Änderung benachrichtigen
    this.emitChange();
  }

  /**
   * Event-Handler für Sync-Statusänderungen des Matrix-Clients.
   * Verwendet queueMicrotask für Debouncing, um gleichzeitige Updates zu vermeiden.
   * @param state - Der neue Sync-Status
   * @param prevState - Der vorherige Sync-Status
   */
  private handleSyncChange = (state: SyncState, prevState: SyncState | null): void => {
    // Verhindere gleichzeitige Updates (Debouncing)
    if (this.pendingUpdate) return;

    this.pendingUpdate = true;
    queueMicrotask(() => {
      this.pendingUpdate = false;

      // Speichere vorherigen State für Vergleich
      const prev = this.state;
      this.updateOnlineStatus(state, prevState);

      // Benachrichtige Listener nur bei tatsächlichen Änderungen
      if (!this.isEqual(prev, this.state)) {
        this.emitChange();
      }
    });
  };

  /**
   * Prüft, ob der gegebene Sync-Status als "bereit" gilt.
   * Ein Client ist bereit, wenn er PREPARED, SYNCING oder CATCHUP ist.
   * @param state - Der zu prüfende Sync-Status
   * @returns True, wenn der Status "bereit" ist
   */
  private isStateReady(state: SyncState | null): boolean {
    return state === SyncState.Prepared || state === SyncState.Syncing || state === SyncState.Catchup;
  }

  /**
   * Bestimmt die Art des Statusübergangs basierend auf vorherigem und aktuellem Status.
   * Kategorisiert Übergänge wie Initial-Sync, Reconnect, Connection-Lost, etc.
   * @param prevState - Der vorherige Sync-Status
   * @param currentState - Der aktuelle Sync-Status
   * @returns Die Art des Statusübergangs
   */
  private getTransitionType(prevState: SyncState | null, currentState: SyncState | null): MatrixStateType {
    if (currentState === null) return null;

    // Initial-Zustände (erster Status-Übergang)
    if (prevState === null) {
      if (currentState === SyncState.Error) return 'initial-error';
      if (this.isStateReady(currentState)) return 'initial-sync';
    }

    // Stopped-Zustand
    if (currentState === SyncState.Stopped) return 'stopped';

    // Wiederverbindung nach Fehler
    if (prevState === SyncState.Error && this.isStateReady(currentState)) {
      return 'reconnected';
    }

    // Verbindungsverlust während aktivem Betrieb
    if (this.isStateReady(prevState) && currentState === SyncState.Error) {
      return 'connection-lost';
    }

    // Übergang in Reconnecting-Zustand
    if (this.isStateReady(prevState) && currentState === SyncState.Reconnecting) {
      return 'reconnecting';
    }

    // Wiederherstellung aus Reconnecting-Zustand
    if (prevState === SyncState.Reconnecting && this.isStateReady(currentState)) {
      return 'reconnected';
    }

    // Normale Übergänge ohne besondere Bedeutung
    return 'normal';
  }

  /**
   * Aktualisiert den internen Status basierend auf dem aktuellen Sync-Status.
   * Berechnet abgeleitete Zustände wie isReady, hasFatalError und showSyncingUI.
   * @param syncState - Der aktuelle Sync-Status (optional, wird vom Client gelesen wenn nicht angegeben)
   * @param prevState - Der vorherige Sync-Status (optional)
   */
  private updateOnlineStatus(syncState?: SyncState, prevState?: SyncState | null): void {
    // Wenn kein Client vorhanden ist, setze auf Initial-State zurück
    if (!this.client) {
      this.state = {
        currentSyncState: null,
        previousSyncState: null,
        status: null,

        isReady: false,
        hasFatalError: false,
        showSyncingUI: true,
      };
      return;
    }

    // Aktuellen Status vom Client abrufen, falls nicht übergeben
    const currentState = syncState ?? this.client.getSyncState();

    // Vorherigen Status verwenden oder aus State lesen
    const previousSyncState = prevState !== undefined ? prevState : this.state.currentSyncState;

    // Art des Übergangs bestimmen
    const status = this.getTransitionType(previousSyncState, currentState);

    // Prüfen, ob Client betriebsbereit ist
    const isReady = this.isStateReady(currentState);

    // Fatale Fehler identifizieren (initial error, stopped, connection lost)
    const hasFatalError =
      status === 'initial-error' ||
      status === 'stopped' ||
      (status === 'connection-lost' && currentState === SyncState.Error);

    // Neuen State zusammenstellen
    this.state = {
      currentSyncState: currentState,
      previousSyncState,
      status,

      isReady,
      hasFatalError,
      showSyncingUI: !isReady && !hasFatalError,
    };
  }

  /**
   * Gibt den aktuellen Status-Snapshot zurück.
   * Wird von React (useSyncExternalStore) verwendet, um den aktuellen Zustand zu lesen.
   * @returns Der aktuelle MatrixOnlineState
   */
  getSnapshot = (): MatrixOnlineState => {
    return this.state;
  };

  /**
   * Registriert einen Listener, der bei Statusänderungen aufgerufen wird.
   * Wird von React (useSyncExternalStore) verwendet, um Updates zu abonnieren.
   * @param listener - Die Callback-Funktion, die bei Änderungen aufgerufen wird
   * @returns Eine Unsubscribe-Funktion zum Entfernen des Listeners
   */
  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * Benachrichtigt alle registrierten Listener über eine Statusänderung.
   * Ruft jeden Listener-Callback auf, um UI-Updates zu triggern.
   */
  private emitChange(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }

  /**
   * Vergleicht zwei MatrixOnlineState-Objekte auf Gleichheit.
   * Wird verwendet, um unnötige Listener-Benachrichtigungen zu vermeiden.
   * @param a - Erster Status-State
   * @param b - Zweiter Status-State
   * @returns True, wenn beide States identisch sind
   */
  private isEqual(a: MatrixOnlineState, b: MatrixOnlineState): boolean {
    return (
      a.status === b.status &&
      a.currentSyncState === b.currentSyncState &&
      a.previousSyncState === b.previousSyncState &&
      a.isReady === b.isReady &&
      a.hasFatalError === b.hasFatalError
    );
  }
}

/**
 * Singleton-Instanz des MatrixStatusStore.
 * Diese Instanz wird in der gesamten Anwendung verwendet, um den Matrix-Status zu verwalten.
 */
export const matrixStatusStore = new MatrixStatusStore();
